import Cookies, { type CookieSetOptions } from 'universal-cookie';

import { logRuntimeError } from 'Diagnostics/runtimeLogger';
import platformHelpers, { type StoragePermissions } from 'Models/uiConfig/platformHelpers';

import keys from './userStorageKeys';

const analyticsKeys = new Set<string>([
  'cmuid',
  'cmsid',
  keys.deferedEvents,
  keys.searchId,
  keys.trackedThemeId,
  keys.themeTelemetryEnabled,
]);

const storagePermissionListeners = new Set<() => void>();
export function subscribeToStoragePermissions(
  permission: keyof StoragePermissions,
  onChange: () => void,
): () => void {
  let allowed = platformHelpers.getStoragePermissions()[permission] === true;
  const listener = () => {
    const nextAllowed = platformHelpers.getStoragePermissions()[permission] === true;
    if (allowed !== nextAllowed) {
      allowed = nextAllowed;
      onChange();
    }
  };
  storagePermissionListeners.add(listener);
  return () => storagePermissionListeners.delete(listener);
}
export function onStoragePermissionsChanged(): void {
  [...storagePermissionListeners].forEach((onChange) => {
    try {
      onChange();
    } catch (error) {
      logRuntimeError({ source: 'storage_permissions', error });
    }
  });
}

export function isFunctionalStorageAllowed(): boolean {
  return platformHelpers.getStoragePermissions().functional === true;
}

const cookies = new Cookies();
export const localStorage = createStorageProxy('localStorage');
export const sessionStorage = createStorageProxy('sessionStorage');

export function isStorageAllowed(key: string): boolean {
  const permissions = platformHelpers.getStoragePermissions();
  return permissions[analyticsKeys.has(key) ? 'analytics' : 'functional'] === true;
}

export function removeCookie(key: string): void {
  try {
    cookies.remove(key, { path: '/' });
  } catch {}
}

export function getCookie(key: string): string | null {
  if (!isStorageAllowed(key)) {
    return null;
  }
  const value: unknown = cookies.get(key, { doNotParse: true });
  return typeof value === 'string' && value !== 'undefined' ? value : null;
}

export function setCookie(key: string, value: unknown, expires?: Date, options?: CookieSetOptions): void {
  if (isStorageAllowed(key)) {
    cookies.set(key, value, { ...options, path: '/', expires });
  }
}

function createStorageProxy(
  name: 'localStorage' | 'sessionStorage',
): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> {
  let localStorageWarned = false;
  function warnLocalStorage(e: unknown): void {
    if (name === 'localStorage' && !localStorageWarned) {
      console.warn(
        '[Convermax] localStorage unavailable — garage, vehicle selection, product history, and view preferences will not be persisted for this session.',
        e,
      );
      localStorageWarned = true;
    }
  }

  return {
    getItem(key: string): string | null {
      if (!isStorageAllowed(key)) {
        return null;
      }
      try {
        return window[name].getItem(key);
      } catch (error) {
        warnLocalStorage(error);
        return null;
      }
    },
    setItem(key: string, value: string): void {
      if (isStorageAllowed(key)) {
        try {
          window[name].setItem(key, value);
        } catch (error) {
          warnLocalStorage(error);
        }
      }
    },
    removeItem(key: string): void {
      try {
        window[name].removeItem(key);
      } catch (error) {
        warnLocalStorage(error);
      }
    },
  };
}

const storageHelpers = {
  getCookie,
  setCookie,
  isStorageAllowed,
};
export type StorageHelpers = typeof storageHelpers;
export default storageHelpers;
